import { useEffect, useState, type ChangeEvent } from 'react';
import type { QueryKey } from '@tanstack/react-query';
import { Box, Switch, Typography, alpha, Alert, Tooltip } from '@mui/material';
import ConfirmationModal from '@/components/common/confirmation-modal';
import { useTranslations } from '@/i18n/use-translations';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useLiveContextFlowToggle } from './use-live-context-flow-toggle';
import { useGoLivePreviewHold } from '@/state/stores/use-go-live-preview-hold.store';

export interface LiveContextModeSwitchProps {
  flow: FlowDto;
  eventSlotId?: string | null;
  flowQueryKey?: QueryKey;
}

/** SIMULATION ↔ LIVE switch with confirmation before toggling live context. */
export function LiveContextModeSwitch({ flow, eventSlotId, flowQueryKey }: LiveContextModeSwitchProps) {
  const t = useTranslations();
  const {
    hasRunningLiveContext,
    isToggling,
    endToggleUi,
    toggleLiveContext,
    canShowToggle,
    canStartLive,
    slotContextRequired,
    playoutBlocksLive,
    previewHoldOwner,
  } = useLiveContextFlowToggle({ flow, eventSlotId, flowQueryKey });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingWantLive, setPendingWantLive] = useState<boolean | null>(null);
  const [goLiveHttpDone, setGoLiveHttpDone] = useState(false);
  const holdOwner = useGoLivePreviewHold((state) => state.owner);
  const previewsHeld = holdOwner === previewHoldOwner;

  useEffect(() => {
    if (!goLiveHttpDone || previewsHeld) {
      return;
    }
    setGoLiveHttpDone(false);
    setConfirmOpen(false);
    setPendingWantLive(null);
    endToggleUi();
  }, [endToggleUi, goLiveHttpDone, previewsHeld]);

  if (slotContextRequired && !hasRunningLiveContext) {
    return (
      <Alert severity="info" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
        {t('flows.detail.control.selectScheduleOccurrenceForLive')}
      </Alert>
    );
  }

  if (!canShowToggle) {
    return null;
  }

  const simulationLabel = t('flows.detail.control.performMode.simulation', { defaultValue: 'SIMULATION' });
  const liveLabel = t('flows.detail.control.performMode.live', { defaultValue: 'LIVE' });
  const pendingStartLive = pendingWantLive === true;
  const switchDisabled = isToggling || (!hasRunningLiveContext && !canStartLive);

  const handleChange = (_: ChangeEvent<HTMLInputElement>, wantLive: boolean) => {
    if (wantLive === hasRunningLiveContext || isToggling) {
      return;
    }
    if (wantLive && !canStartLive) {
      return;
    }
    setPendingWantLive(wantLive);
    setConfirmOpen(true);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingWantLive(null);
  };

  const handleConfirm = () => {
    const startingLive = pendingWantLive === true;
    const previewConsumerMounted = useGoLivePreviewHold.getState().consumers > 0;
    toggleLiveContext({
      holdUi: startingLive && previewConsumerMounted,
      onSettled: () => {
        if (startingLive) {
          setGoLiveHttpDone(true);
          return;
        }
        setConfirmOpen(false);
        setPendingWantLive(null);
      },
    });
  };

  const confirmTitle = pendingStartLive
    ? t('flows.detail.control.performMode.goLiveTitle', { defaultValue: 'Go live' })
    : t('flows.detail.control.performMode.returnToSimulationTitle', { defaultValue: 'Return to simulation' });
  const confirmText = pendingStartLive
    ? t('flows.detail.control.performMode.goLiveText', {
        defaultValue: 'Start live playout so connected players can receive commands and media.',
      })
    : t('flows.detail.control.performMode.returnToSimulationText', {
        defaultValue: 'End live playout and switch back to simulation mode.',
      });
  const confirmLabel = pendingStartLive
    ? t('flows.detail.control.performMode.goLiveConfirm', { defaultValue: 'Go live' })
    : t('flows.detail.control.performMode.returnToSimulationConfirm', { defaultValue: 'End live' });

  const switchControl = (
    <Box
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        flexShrink: 0,
        border: 1,
        borderColor: hasRunningLiveContext ? 'error.main' : 'divider',
        borderRadius: 1,
        px: 0.75,
        py: 0.125,
        bgcolor: hasRunningLiveContext ? 'error.main' : 'background.default',
        color: hasRunningLiveContext ? 'error.contrastText' : 'text.primary',
        opacity: switchDisabled && !hasRunningLiveContext ? 0.72 : 1,
        transition: theme.transitions.create(['background-color', 'border-color', 'color', 'opacity'], {
          duration: theme.transitions.duration.shortest,
        }),
      })}
    >
      <Typography
        variant="caption"
        component="span"
        sx={(theme) => ({
          fontSize: '0.65rem',
          fontWeight: hasRunningLiveContext ? 500 : 700,
          letterSpacing: 0.4,
          color: hasRunningLiveContext ? alpha(theme.palette.error.contrastText, 0.72) : 'text.primary',
          userSelect: 'none',
        })}
      >
        {simulationLabel}
      </Typography>
      <Switch
        size="small"
        checked={hasRunningLiveContext}
        disabled={switchDisabled}
        onChange={handleChange}
        inputProps={{
          'aria-label': hasRunningLiveContext
            ? t('flows.detail.control.performMode.switchToSimulation', {
                defaultValue: 'Switch to simulation',
              })
            : t('flows.detail.control.performMode.switchToLive', { defaultValue: 'Switch to live' }),
        }}
        sx={(theme) => ({
          mx: -0.25,
          ...(hasRunningLiveContext
            ? {
                '& .MuiSwitch-switchBase.Mui-checked': {
                  'color': 'error.contrastText',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.contrastText, 0.08),
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: alpha(theme.palette.error.contrastText, 0.55),
                  opacity: 1,
                },
                '& .MuiSwitch-switchBase:not(.Mui-checked)': {
                  color: alpha(theme.palette.error.contrastText, 0.85),
                },
                '& .MuiSwitch-track': {
                  backgroundColor: alpha(theme.palette.error.contrastText, 0.35),
                  opacity: 1,
                },
              }
            : {}),
        })}
      />
      <Typography
        variant="caption"
        component="span"
        sx={{
          fontSize: '0.65rem',
          fontWeight: hasRunningLiveContext ? 700 : 500,
          letterSpacing: 0.4,
          color: hasRunningLiveContext ? 'error.contrastText' : 'text.secondary',
          userSelect: 'none',
        }}
      >
        {liveLabel}
      </Typography>
    </Box>
  );

  return (
    <>
      {playoutBlocksLive && !hasRunningLiveContext ? (
        <Tooltip title={t('flows.detail.control.insufficientPlayoutHoursTooltip')}>{switchControl}</Tooltip>
      ) : (
        switchControl
      )}

      <ConfirmationModal
        open={confirmOpen}
        title={confirmTitle}
        text={confirmText}
        confirmLabel={confirmLabel}
        isLoading={isToggling}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
